// ════════════════════════════════════════════════════════════════════════
//  Die Führung durch die ersten Schritte der Redaktion
//
//  Wer neu in der Redaktion ist, wird beim ersten Anmelden durch die Admin-App
//  getragen. Am Ende weiß die Person, WO DIE VORGÄNGE LIEGEN: dass die
//  Fassungsleiste der einzige Ort für Entwurf, Speichern und Veröffentlichen
//  ist, dass die Grundlagen die Voraussetzung jeder Lektion sind, dass Hinweise
//  Wege sind und dass der Weg nach Live eine Datei ist, die durch einen
//  Menschen geht.
//
//  WARUM DIESE FÜHRUNG ÜBERWIEGEND EINE LESEFÜHRUNG IST
//
//  Die Führung der Lehrkraft endet mit einem GEGENSTAND: einer echten Klasse
//  und gedruckten Kärtchen. Genau das kann diese hier nicht wollen. Jede
//  veröffentlichte Fassung ist unveränderlich, jeder ausgelieferte Schlüssel ist
//  es auch - ein „Übungslehrgang“, den man hinterher wegräumt, ist hier kein
//  harmloser Handgriff, sondern Müll im Bestand.
//
//  Genau EIN Handgriff ist echt und folgenlos rücknehmbar: „Neuer Entwurf“ und
//  „Verwerfen“. Der ist Station 2, und er ist die einzige gemessene Station.
//  Alle anderen sind Blicke und sagen das auch - sie tragen `done: null` und
//  haben deshalb einen Weiter-Knopf. Damit fällt die Frage „gibt es hier einen
//  Knopf?“ mit der Frage „ist das gemessen?“ zusammen, wie in der Verwaltung.
//
//  DER VERWERFEN-HALBSATZ GEHÖRT IN STATION 2 SELBST
//
//  Ohne ihn drückt niemand auf „Neuer Entwurf“ - und dann ist die einzige
//  gemessene Station tot. Er steht deshalb im Text der Station und nicht in
//  einer Fußnote irgendwo.
//
//  WAS HIER STEHT UND WAS IN @otto/onboarding
//
//  Hier: die Stationen dieser App, ihre Texte, ihre Marken. Dort: die Rechnung -
//  Lesezeichen, Prüfliste, Weiterschalten. Die Regeln sind in beiden Apps
//  dieselben, die Stationen in keiner.
// ════════════════════════════════════════════════════════════════════════

import type { TourStep as TourStepBase } from "@otto/onboarding";
import { COURSES } from "../../../shared/lib/coursePath.js";

/**
 * Die Stationen. Sprechende Schlüssel sind hier richtig - anders als bei
 * Lektionen und Stufen liegen sie in keiner Datenbankzeile eines Kindes,
 * sondern in einer Zeile, die zur nächsten Fassung der App gehört.
 */
export type TourStepKey =
  | "lehrgaenge"
  | "entwurf"
  | "grundlagen"
  | "lektion"
  | "veroeffentlichen"
  | "uebertragen"
  | "schulen"
  | "fertig";

/**
 * Die Marken, die an echten Bedienelementen stehen. Ein eigener Typ, weil zu
 * jeder Marke eine CSS-Regel gehört: eine Marke, die nur in der Liste steht,
 * hebt nichts hervor, und das fällt niemandem auf. Der Wächter in
 * `TourBar.test.tsx` prüft beide Seiten gegeneinander.
 */
export type TourMark =
  | "lehrgang-oeffnen"
  | "neuer-entwurf"
  | "neue-lektion"
  | "veroeffentlichen"
  | "uebertragen";

/** Woran sich eine Station abhaken lässt – alles aus vorhandenen Abfragen. */
export type TourFacts = {
  /**
   * Hat DIESE Person irgendwo einen Entwurf angelegt? Aus `useCurricula`, das
   * `created_by` der Fassungen ohnehin mitbringt.
   *
   * Nicht „hat der Lehrgang einen Entwurf“: ein Entwurf gehört dem Lehrgang,
   * und öffnete ihn eine Kollegin, hakte er sich bei allen ab. Deshalb die
   * Spalte `curriculum_versions.created_by`.
   */
  readonly hasOwnDraft: boolean;
  /**
   * Der Lehrgang, auf den die Führung zeigt, oder null.
   *
   * Null ist hier ein echter Fall und nicht nur der leere Bestand: ein Lehrgang
   * hat höchstens EINEN Entwurf, und ist jeder durch einen fremden belegt, gibt
   * es nichts zu zeigen. Dann hat die Station kein Ziel und keine Marke - ein
   * Ring um „Neuer Entwurf“ zeigte sonst auf einen Knopf, den es dort nicht gibt.
   */
  readonly slug: string | null;
};

/**
 * Eine Station dieser App.
 *
 * Die Rechnung kommt aus `@otto/onboarding`; hier wird sie nur enger gefasst:
 * der Schlüssel ist eine Union statt `string`, die Marke ebenso, und die Fakten
 * sind die der Admin-App.
 */
export type TourStep = TourStepBase<TourStepKey, TourFacts> & {
  readonly mark: TourMark | null;
};

/**
 * Der Willkommenssatz. Teil der Leiste, keine eigene Station: eine Tour über
 * die Tour ist ein Schritt, der nichts hinterlässt.
 */
export const TOUR_WELCOME =
  "Willkommen in der Redaktion. Wir sehen uns einmal an, wo die Vorgänge liegen – " +
  "und wo sie ausdrücklich nicht liegen. Angelegt wird dabei nichts, was Sie nicht " +
  "mit einem Klick wieder loswerden.";

const COURSE = `${COURSES}/:slug`;

export const TOUR_STEPS: readonly TourStep[] = [
  {
    key: "lehrgaenge",
    title: "Die Lehrgänge sind eine Liste",
    text:
      "Jeder Lehrgang hat seine eigenen Fassungen, seinen eigenen Entwurf – höchstens " +
      "einen – und seine eigenen Stammdaten. Die Fassungen zählt jeder Lehrgang für " +
      "sich; eine Fassungsnummer ohne den Lehrgang dazu sagt deshalb nichts. Über " +
      "„Öffnen“ geht es hinein.",
    route: COURSES,
    mark: "lehrgang-oeffnen",
    done: null,
  },
  {
    key: "entwurf",
    title: "Nachsehen geht immer, Ändern braucht einen Entwurf",
    text:
      "Ohne Entwurf steht die zuletzt veröffentlichte Fassung vollständig da – " +
      "schreibgeschützt, aber nicht leer. Nachschlagen, was in einer Lektion steht, ist " +
      "der häufigste Grund, diese App zu öffnen. Zum Ändern nimmt „Neuer Entwurf“ in der " +
      "Fassungsleiste den Schutz weg. Probieren Sie es aus: „Verwerfen“ daneben nimmt " +
      "den Entwurf spurlos wieder zurück. Die Leiste ist der einzige Ort für diese " +
      "Vorgänge, und es gibt kein automatisches Speichern.",
    route: `${COURSE}/uebersicht`,
    mark: "neuer-entwurf",
    // Die EINZIGE gemessene Station: sie hakt sich daran ab, dass diese Person
    // einen Entwurf angelegt hat - nicht daran, dass irgendwo einer offen ist.
    done: (facts) => facts.hasOwnDraft,
  },
  {
    key: "grundlagen",
    title: "Erst die Taste, dann die Lektion",
    text:
      "Unter „Tastatur und Finger“ gehört jede Taste einem der neun Finger. Eine " +
      "Taste, die dort fehlt, kann keine Lektion benutzen – der Hinweis " +
      "„Zeichen … hat keine Finger-Zuordnung“ erscheint dann an der Stufe, behoben " +
      "wird er hier. Daneben liegen die Sterne-Schwellen und der Test; sie gelten " +
      "für den ganzen Lehrgang.",
    route: `${COURSE}/grundlagen/tastatur`,
    mark: null,
    done: null,
  },
  {
    key: "lektion",
    title: "Eine Lektion entsteht im Assistenten",
    text:
      "„Neue Lektion“ führt in fünf Schritten durch das Anlegen und schreibt erst im " +
      "letzten in den Entwurf. Einen Knopf, der eine leere Lektion anhängt, gibt es " +
      "nicht: Platzhalter sind gültig und veröffentlichbar und gleichen dem Bestand in " +
      "nichts. Umsortiert wird in der Liste durch Ziehen, mit Alt + Pfeil als " +
      "Tastaturweg.",
    route: `${COURSE}/lektionen`,
    mark: "neue-lektion",
    done: null,
  },
  {
    key: "veroeffentlichen",
    title: "Prüfen und veröffentlichen",
    text:
      "Hinweise sind Wege: ein Klick springt in das Feld, um das es geht, und setzt den " +
      "Fokus. Geprüft wird nur der Entwurf – an einem gesperrten Feld wäre eine " +
      "Hinweiszahl eine Aufgabe ohne Knopf. Veröffentlichen ist endgültig: die Fassung " +
      "ist danach unveränderlich. Klassen wechseln nicht von selbst mit; die neue " +
      "Fassung gilt für neu angelegte Klassen.",
    route: `${COURSE}/uebersicht`,
    mark: "veroeffentlichen",
    done: null,
  },
  {
    key: "uebertragen",
    title: "Von Staging nach Live ist eine Datei",
    text:
      "Unter „Übertragen“ auf der Lehrgangsliste entsteht eine Exportdatei, und dort " +
      "wird sie in der anderen Umgebung wieder eingelesen. Kein Dienst kennt beide " +
      "Umgebungen – das ist keine Sparversion, sondern die einzige Form, die keine " +
      "Verbindung zwischen Produktion und Testsystem aufmacht. Der Import legt einen " +
      "Entwurf an und veröffentlicht nicht. Hier nur ansehen: eingelesen wird in Live.",
    route: COURSES,
    mark: "uebertragen",
    done: null,
  },
  {
    key: "schulen",
    title: "Schulen – der zweite Bereich",
    text:
      "Stammdaten, Lizenzzeiträume, Abrechnungsvermerk und die Einladung des " +
      "Schuladmins. Was diese App ausdrücklich nicht sieht, sind Schülerdaten: es gibt " +
      "niemanden auf der Plattform, der alle Kinder sehen kann. Auch die Klassenzahl " +
      "einer Schule ist nur eine Zahl, keine Liste.",
    route: "/schools",
    mark: null,
    done: null,
  },
  {
    key: "fertig",
    title: "Fertig – der Rest steht in der Anleitung",
    text:
      "Die vollständige Anleitung „Eine neue Lektion anlegen“ können Sie hier als PDF " +
      "herunterladen: sie beschreibt den Weg Schritt für Schritt, samt Großbuchstaben, " +
      "Test und dem, was nach der Veröffentlichung nicht mehr zu ändern ist. Diese " +
      "Leiste holen Sie jederzeit über „Erste Schritte“ im Kopf der Seite zurück.",
    route: null,
    mark: null,
    done: null,
  },
];

/**
 * Die Platzhalter dieser App für `tourRoute`.
 *
 * Eine eigene Funktion, damit der Name `slug` an genau einer Stelle steht. Die
 * Verwaltung bringt `classId` mit – deshalb kennt das Paket keinen der beiden.
 */
export function tourParams(facts: TourFacts): Record<string, string | null> {
  return { slug: facts.slug };
}
